import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Chip,
  Grid,
  TypographyProps,
  Autocomplete,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  AutocompleteRenderInputParams,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  TP_COLORS,
  questionDifficultyOptions,
  ISelectedDataOption,
  ISelectedOption,
  allSelectedOption,
} from '../../../../constants';
import { getSubTopicState } from '../../../../store/SubTopicSlice';
import { adminCreateQuiz } from '../../../../store/QuizSlice';
import { TQuestionDifficulty } from '../../../../store/QuestionSlice';
import { useAppSelector } from '../../../../hooks';
import { getSubjectColor } from '../../../../helper';
import AddNewSubtopicToQuizModal from './AddNewSubtopicToQuizModal';
import DetailViewCard from '../../../../components/Card/DetailViewCard';

interface ISelectOption {
  label: string;
  value: string;
}

export interface IAddNewQuizDrawer {
  open: boolean;
  onClose: () => void;
  subject: string;
  curriculum: string;
  grade: string;
  subjectCode: string;
  topicData: ISelectedDataOption;
  subTopicData: ISelectedDataOption;
  handleTopicChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectOption
  ) => void;
}

interface ICustomQuizSubTopic {
  id: string;
  name: string;
  paper: string;
}

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

const AddNewQuizDrawer: React.FC<IAddNewQuizDrawer> = ({
  open,
  onClose,
  subject,
  curriculum,
  subjectCode,
  topicData,
  subTopicData,
  handleTopicChange,
}) => {
  const dispatch = useDispatch();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);

  // State
  const [isAddSubTopicModalOpen, setSubTopicModalOpen] = useState(false);
  const [createAsAssessment, setCreateAsAssesment] = useState(false);
  const [questionNumber, setQuestionNumber] = useState('');

  const [questionDifficulty, setQuestionDifficulty] = useState(
    questionDifficultyOptions[0]
  );

  const [quizSubTopics, setQuizSubTopics] = useState<
    Array<ICustomQuizSubTopic>
  >([]);

  let subTopicOptions = subTopicData.options.filter(
    (subTopic) => subTopic !== allSelectedOption
  );

  if (quizSubTopics.length) {
    const firstItem = quizSubTopics[0];

    subTopicOptions = subTopicOptions.filter(
      (item) => item.value === firstItem.id
    );
  }

  const onTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    handleTopicChange(event, value);

    setQuizSubTopics([]);
  };

  const toggleAddNewSubTopicModal = () => {
    setSubTopicModalOpen(!isAddSubTopicModalOpen);
  };

  const handleSubmit = () => {
    const formatedSubTopics = (quizSubTopics || []).map((quizSubTopic) => ({
      id: quizSubTopic.id,
      paper: quizSubTopic.paper,
    }));

    dispatch(
      adminCreateQuiz({
        topicId: topicData.selectedItem.value,
        noOfQuestions: Number(questionNumber),
        difficulty: questionDifficulty,
        assessment: createAsAssessment,
        subTopics: formatedSubTopics,
      })
    );

    onClose();
  };

  const handleCheckChange = (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    setCreateAsAssesment(checked);
  };

  const handleSubmitQuizSubTopic = (item: ICustomQuizSubTopic) => {
    const isExists = quizSubTopics.some(
      (quizSubTopic) =>
        quizSubTopic.id === item.id && quizSubTopic.paper === item.paper
    );

    if (!isExists) {
      const newQuizSubTopics = [...quizSubTopics, item];

      setQuizSubTopics(newQuizSubTopics);
    }
  };

  const handleDeleteQuizSubTopic = (item: ICustomQuizSubTopic) => {
    const newQuizSubTopics = [...quizSubTopics].filter(
      (quizSubTopic) => quizSubTopic !== item
    );

    setQuizSubTopics(newQuizSubTopics);
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField {...params} placeholder="Select" />
  );

  const drawerHeader = (
    <Stack
      sx={{
        background: getSubjectColor(subject),
        p: '30px 15px',
        borderBottomLeftRadius: '7px',
        borderBottomRightRadius: '7px',
      }}
    >
      <Typography
        fontWeight={500}
        className="ellipsis-text"
        sx={{ color: TP_COLORS.white, opacity: 0.5 }}
      >
        {curriculum}
      </Typography>
      <Typography
        className="ellipsis-text"
        variant="h6"
        sx={{ color: TP_COLORS.white }}
      >
        {subject}
      </Typography>
      <Chip
        size="small"
        label={subjectCode}
        sx={{
          color: TP_COLORS.white,
          backgroundColor: 'rgba(255,255,255, 0.2)',
          borderRadius: '4px',
          width: 'fit-content',
          fontWeight: 600,
        }}
      />
    </Stack>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ style: { minWidth: '400px', maxWidth: '400px' } }}
      >
        <Box role="presentation">
          {drawerHeader}
          <div style={{ padding: '30px 15px 15px 15px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography {...label}>Topic *</Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  renderInput={renderInput}
                  onChange={onTopicChange}
                  options={topicData.options}
                  value={topicData.selectedItem}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography {...label}>No Of Questions *</Typography>
                <FormControl fullWidth>
                  <TextField
                    size="small"
                    type="number"
                    value={questionNumber}
                    placeholder="No Of Questions"
                    onChange={(event) =>
                      setQuestionNumber(event.currentTarget.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Typography {...label}>
                    Question Difficulty Level *
                  </Typography>
                  <Select
                    size="small"
                    value={questionDifficulty}
                    onChange={(event) => {
                      const option = event.target.value as TQuestionDifficulty;
                      setQuestionDifficulty(option);
                    }}
                  >
                    {[...questionDifficultyOptions, 'ANY'].map(
                      (difficultyOption, index) => (
                        <MenuItem key={index} value={difficultyOption}>
                          {difficultyOption}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={<Checkbox checked={createAsAssessment} />}
                    label="Create as an assesment"
                    onChange={handleCheckChange}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  onClick={toggleAddNewSubTopicModal}
                  startIcon={<AddIcon />}
                  disabled={subTopicState.status === 'loading'}
                >
                  Add Sub Topic
                </Button>
              </Grid>
              {quizSubTopics.length ? (
                <>
                  <Grid item xs={12}>
                    <Typography {...label} variant="h6">
                      Sub Topics
                    </Typography>
                  </Grid>
                  {quizSubTopics.map((quizSubTopic, index) => {
                    const descriptiontext = `${quizSubTopic.name} - ${quizSubTopic.paper}`;

                    return (
                      <Grid key={index} item xs={12}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <div style={{ width: 'calc(100% - 50px)' }}>
                            <DetailViewCard
                              title="SubTopic"
                              description={descriptiontext}
                              containerProps={{ sx: { mb: 0 } }}
                              titleProps={{ fontWeight: 600 }}
                            />
                          </div>
                          <IconButton
                            color="error"
                            onClick={() =>
                              handleDeleteQuizSubTopic(quizSubTopic)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Grid>
                    );
                  })}
                </>
              ) : null}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={
                        questionNumber.trim() === '' || !quizSubTopics.length
                      }
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </Box>
      </Drawer>
      {isAddSubTopicModalOpen ? (
        <AddNewSubtopicToQuizModal
          open={isAddSubTopicModalOpen}
          onClose={toggleAddNewSubTopicModal}
          defaultSubTopicId={subTopicData.selectedItem.value}
          onSubmit={handleSubmitQuizSubTopic}
          subTopics={subTopicOptions}
        />
      ) : null}
    </>
  );
};

export default AddNewQuizDrawer;
